import './recover-password.screen.scss';
import {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Divider, Typography, Button, Box} from '@mui/material';
import {PublicContentPage} from '../../../../components/public-content-page/public-content-page';
import {LegalLinksStack} from '../../../../components/legal-links-stack/legal-links-stack';
import {useHistory} from 'react-router-dom';
import {ScreenPaths} from '../../../../enums/screen-path';
import {translations} from '../../../../i18n/translations';
import {AppContextType} from '../../../../contexts/app/types';
import {AppContext} from '../../../../contexts/app/context';
import {PostRecoverPassword} from '../../../../backend/requests/post-recover-password';
import {AuthenticationRepository} from '../../../../repositories/authentication';
import {SnackBarContextType} from '../../../../contexts/snack-bar/types';
import {SnackBarContext} from '../../../../contexts/snack-bar/context';
import {TextInput} from '../../../../components/text-input/text-input';
import LoadingButton from '@mui/lab/LoadingButton';
import SendIcon from '@mui/icons-material/Send';

export default function RecoverPasswordScreen() {
  // Get context
  const appContext: AppContextType = useContext(AppContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Translations
  const {t} = useTranslation();

  // Get history
  let history = useHistory();

  // Hold state
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [showEmailError, setShowEmailError] = useState<boolean>(false);

  // Validation
  useEffect(() => {
    // Email validation
    if (email.trim().length > 0) {
      // No errors
      if (emailError !== undefined) {
        setEmailError(undefined);
      }

      // Enable button
      if (buttonDisabled !== false) {
        setButtonDisabled(false);
      }
    } else {
      // Error
      if (showEmailError) {
        setEmailError(t(translations.recover_password.errors.form_validation_invalid_field));
      }

      // Disable button
      if (buttonDisabled !== true) {
        setButtonDisabled(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, showEmailError]);

  // Recover password
  const handleRecoverPassword = async (event: any) => {
    event.preventDefault();

    // Reset form errors
    setEmailError(undefined);

    // Everything looks ok, let's perform the request. Start loading.
    setSubmitting(true);

    try {
      // Request body
      const requestBody: PostRecoverPassword = {
        email: email.trim(),
        product: appContext.state.configuration.product,
      };

      // Send request
      await AuthenticationRepository.instance.recover(requestBody);

      // Everything went well
      snackBarContext.setPublicToastNotification({
        message: t(translations.recover_password.success.send_request_success_message),
        severity: 'success',
      });

      // Go to the login screen
      history.push(ScreenPaths.Login);
    } catch (error) {
      // Error
      snackBarContext.setPublicToastNotification({
        message: t(translations.recover_password.errors.send_request_error_message),
        severity: 'error',
      });
    }

    // Stop loading
    setSubmitting(false);
  };

  // Navigate to the login screen
  const handleNavigateToLogin = () => {
    // Navigate
    history.push(ScreenPaths.Login);
  };

  // Handle change email input
  const handleChangeEmail = (value: string | undefined | null) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set email with the new value
    setEmail(finalValue);
  };

  // Handle blur email input
  const handleBlurEmail = () => {
    if (!showEmailError) {
      setShowEmailError(true);
    }
  };

  return (
    <PublicContentPage>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
        }}
        className="recover-password-title"
      >
        {t(translations.recover_password.title)}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
        }}
        className="recover-password-title"
      >
        {t(translations.recover_password.subtitle)}
      </Typography>
      <Typography variant="body1" className="recover-password-instructions">
        {t(translations.recover_password.instructions)}
      </Typography>
      <form onSubmit={handleRecoverPassword}>
        <Box className="recover-password-input">
          <TextInput
            label={t(translations.recover_password.email_input)}
            disabled={submitting}
            value={email}
            handleChange={handleChangeEmail}
            handleBlur={handleBlurEmail}
            error={emailError}
            required={true}
            type="email"
          />
        </Box>
        <LoadingButton
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          size="medium"
          loading={submitting}
          loadingPosition="end"
          endIcon={<SendIcon />}
          disabled={buttonDisabled}
        >
          {t(translations.recover_password.send_button)}
        </LoadingButton>
      </form>
      <Divider className="recover-password-divider" />
      <Box className="recover-password-remembered-container">
        <Typography className="recover-password-remembered-title" variant="body1">
          {t(translations.recover_password.password_remembered)}
        </Typography>
        <Button
          className="recover-password-remembered-button"
          disabled={submitting}
          variant="text"
          color="inherit"
          size="medium"
          onClick={handleNavigateToLogin}
        >
          {t(translations.recover_password.go_back_button)}
        </Button>
      </Box>
      <LegalLinksStack paddingTop="44px" />
    </PublicContentPage>
  );
}
